import React, { useMemo, useState, useEffect } from 'react';
import { Box, Stack, Anchor, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableColumns } from '@fortawesome/free-solid-svg-icons';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { config } from '@fortawesome/fontawesome-svg-core';
config.autoAddCss = false;
import { MantineProvider } from '@mantine/core';

const TrainingProgTable = ({ tableData, reactVerbosity, emptyMsg = "You have not completed any training modules yet. " }) => {
  const [columnPinning, setColumnPinning] = useState({});
  const isDesktop = useMediaQuery('(min-width: 1200px)');
  // console.log('TrainingProgTable RENDERING');
  reactVerbosity === 2 &&
    console.log('###################> TrainingProgTable DATA:', tableData);
  const countModules = () => {
    let count = 0;
    let completed = 0;
    tableData.map((c, i) => {
      if (c.userScore >= 0) {
        completed++;
      }
      return count++;
    });
    return { completed, count };
  };
  const { completed, count } = countModules();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isDesktop) {
        setColumnPinning({
          left: ['mrt-row-expand', 'mrt-row-numbers', 'stateOption'],
          right: ['link'],
        });
      } else {
        setColumnPinning({});
      }
    }
  }, [isDesktop]);

  const columns = useMemo(() => [
    {
      header: 'Chapter',
      accessorKey: 'chapter',
      size: 180,
    },
    {
      header: 'Content',
      accessorKey: 'module',
      size: 180,
    },
    {
      header: 'Complete',
      accessorKey: 'complete',
      size: 100,
      colourOptions: [
        { value: '\u2713', label: 'Complete', color: 'green', isFixed: true }
      ],
      Footer: () => (
        <Stack
          style={{
            marginRight: '150px',
            display: 'flex',
            justifyContent: 'left',
            alignItems: 'center'
          }}
        >
          Progress:
          <Box color="orange">
            {completed}/{count}
          </Box>
        </Stack>
      ),
      Cell: ({ cell }) => (
        <Box
          sx={(theme) => ({
            // backgroundColor:
            //   cell.getValue() === "\u2713"
            //     ? theme.colors.red[9]
            //     : theme.colors.green[9],
            // color: cell.getValue() === "1"
            borderRadius: '4px',
            color:
              cell.getValue() === '\u2713' ? theme.colors.green[9] : theme.colors.red[9],
            maxWidth: '9ch',
            padding: '4px'
          })}
        >
          {cell.getValue()}
        </Box>
      )
    },
    {
      header: 'Date',
      accessorKey: 'date',
      size: 100,
    },
    {
      header: 'Module Type   ',
      accessorKey: 'moduleType',
      size: 100,
    }
  ]);

  const table = useMantineReactTable({
    columns,
    data: tableData,
    enableColumnResizing: true,
    enableGrouping: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    initialState: {
      density: 'xs',
      expanded: true,
      grouping: ['chapter'],
      pagination: { pageIndex: 0, pageSize: 30 },
      sorting: [
        { id: 'complete', desc: true },
        { id: 'date', desc: false }
      ]
    },
    mantineTableContainerProps: { 
      sx: { 
        height: '64%',
        maxHeight: 'calc(80vh * 0.8)',
        paddingLeft: '16px',  // Add left padding
        paddingRight: '16px', // Add right padding
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
      } 
    },
    renderEmptyRowsFallback: () => <Text style={{ display: "flex", justifyContent: "center"}}>{emptyMsg}</Text>,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnDragging: false,
    mantineTopToolbarProps: {
      sx: {
        backgroundColor: 'white',
        borderRadius: '8px 8px 0 0',
        paddingTop: '0px',
        marginTop: '8px',
        marginBottom: '0px',
      },
    },
    mantineTableProps: {
      sx: {
        borderRadius: '8px 8px 0 0',
        overflow: 'hidden',
        flex: 1, // Allow table to fill remaining space
      },
    },
    mantineToolbarAlertBannerProps: {
      color: 'blue',
    },
    positionToolbarAlertBanner: 'bottom',
    mantinePaperProps: {
      sx: {
        '& .mantine-ActionIcon-root': {
          backgroundColor: '#e9ecef !important',
          borderRadius: '8px !important',
          margin: '0 4px',
          width: '32px !important',
          height: '32px !important',
          '&:hover': {
            backgroundColor: '#dee2e6 !important',
          },
          '& svg': {
            width: '16px !important',
            height: '16px !important',
          },
          // '& .mantine-TableFooter-root': {
          //   backgroundColor: '#e9ecef',
          //   // other styles...
          // },
        },
        '& .mantine-TableFooter-root': {
          backgroundColor: '#e9ecef !important',
        },
        '& .mantine-TableFooter-root td': {
          fontWeight: 'bold !important',
          color: '#333 !important',
          padding: '12px 16px !important',
        },
        '& .mantine-Table-root + div': {
          backgroundColor: '#e9ecef !important',
          padding: '8px 16px',
        },
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        backgroundColor: '#e9ecef',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        color: '#333',
        padding: '8px 6px',
      },
    },
    mantineTableBodyCellProps: {
      sx: {
        padding: '8px 6px', // Adjust padding for body cells
      },
    },
    // mantineTableFooterCellProps: {
    //   sx: {
    //     backgroundColor: '#e9ecef',
    //     fontWeight: 'bold',
    //     // other styles...
    //   },
    // },
    mantineTableFooterCellProps: {
      sx: {
        backgroundColor: '#e9ecef',
        fontWeight: 'bold',
        color: '#333',
        padding: '12px 16px',
      },
    },
    mantineTableFooterRowProps: {
      sx: {
        backgroundColor: '#e9ecef',
        '&:hover': {
          backgroundColor: '#dee2e6',
        },
      },
    },
    icons: {
      IconColumns: () => (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          width="18px"
          height="18px"
        >
          <path d="M0 0h24v24H0z" fill="none"/>
          <circle cx="3.5" cy="7" r="1.5"/>
          <rect x="8" y="6" width="13" height="2"/>
          <circle cx="3.5" cy="12" r="1.5"/>
          <rect x="8" y="11" width="13" height="2"/>
          <circle cx="3.5" cy="17" r="1.5"/>
          <rect x="8" y="16" width="13" height="2"/>
        </svg>
      ),
      IconFilter: () => (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          stroke="currentColor"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18px"
          height="18px"
        >
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
      ),
      IconSearch: () => (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          stroke="currentColor"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="18px"
          height="18px"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      )
    },
    mantineBottomToolbarProps: {
      sx: {
        backgroundColor: '#e9ecef !important',
        borderRadius: '0 0 8px 8px', // Add border radius to bottom corners
        margin: '8px 16px', // Match the side padding
      },
    },
  });

  return (
    <MantineProvider
      theme={{
        components: {
          TableFooter: {
            styles: (theme) => ({
              root: {
                backgroundColor: '#e9ecef',
              },
            }),
          },
        },
      }}
    >
      <div className="custom-table-wrapper">
        <style jsx global>{`
          .mantine-TableFooter-root,
          .mantine-Table-root + div {
            background-color: #e9ecef !important;
          }
          .mantine-TableFooter-root td,
          .mantine-Table-root + div {
            font-weight: bold !important;
            color: #333 !important;
            padding: 12px 16px !important;
          }
        `}</style>
        <MantineReactTable table={table} />
      </div>
    </MantineProvider>
  );
};

export default React.memo(TrainingProgTable);

// enableColumnFilters: false, // Disables column filters
//   enableGlobalFilter: false, // Disables global search
//   enableColumnOrdering: false, // Disables column reordering
//   enablePinning: false, // Disables column pinning
//   enableRowSelection: false, // Disables row selection
//   enableColumnActions: false, // Disables column action menus
//   enableSorting: false, // Disables sorting
//   enablePagination: false, // Disables pagination
//   enableBottomToolbar: false, // Hides the bottom toolbar
//   enableTopToolbar: false, // Hides the top toolbar
//   enableRowActions: false, // Disables row actions
//   enableRowNumbers: false, // Disables row numbers
//   enableHiding: false, // Disables column hiding
//   enableColumnVirtualization: false, // Disables column virtualization
//   enableRowVirtualization: false, // Disables row virtualization
